/**
 * Theme primitives shared by the client provider and the inline bootstrap
 * script rendered in the root layout.
 *
 * Colors themselves live in `src/styles/globals.css` as semantic tokens with a
 * `.dark` override block; everything here only decides whether that `.dark`
 * class is on `<html>`. Components should never branch on the theme — style
 * with the semantic tokens and both themes follow automatically.
 */

export const THEME_STORAGE_KEY = "rhythms-theme"

const THEME_PREFERENCES = ["system", "light", "dark"] as const

/** What the user picked. `system` follows the OS setting. */
export type ThemePreference = (typeof THEME_PREFERENCES)[number]

/**
 * Browsers with no stored choice follow the OS setting. An explicit stored
 * preference takes precedence.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system"

/** What is actually painted once `system` is resolved. */
export type ResolvedTheme = "light" | "dark"

export const DARK_COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)"

export function isThemePreference(value: unknown): value is ThemePreference {
  return THEME_PREFERENCES.includes(value as ThemePreference)
}

export function resolveTheme(
  preference: ThemePreference,
  prefersDark: boolean
): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light"
  return preference
}

/**
 * Paint the resolved theme. `color-scheme` keeps browser-rendered UI
 * (scrollbars, native form controls, autofill) in step with the tokens.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement
  root.classList.toggle("dark", resolved === "dark")
  root.style.colorScheme = resolved
}

/**
 * Runs before first paint so the correct theme is on `<html>` for the very
 * first frame — without it every load flashes light before hydration.
 *
 * Mirrors `readStoredPreference` + `resolveTheme` exactly, including the
 * default: the two must agree or the provider repaints on mount and the load
 * flashes. Only the storage read is wrapped, because only it throws (cookies
 * blocked in Safari private browsing, embedded MS Teams tabs) — and an
 * unreadable store means no stored choice, which is the default, not light.
 */
export const THEME_INIT_SCRIPT = `
(function () {
  var preference = ${JSON.stringify(DEFAULT_THEME_PREFERENCE)};
  try {
    var stored = localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});
    if (${JSON.stringify(THEME_PREFERENCES)}.indexOf(stored) !== -1) {
      preference = stored;
    }
  } catch (error) {
    // Storage is unreadable, so there is no stored choice to honour and
    // preference keeps the default it was initialised with.
  }
  var isDark =
    preference === "dark" ||
    (preference === "system" &&
      window.matchMedia(${JSON.stringify(DARK_COLOR_SCHEME_QUERY)}).matches);
  var root = document.documentElement;
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
})();
`
